import {SxProps, Theme} from '@mui/material';

// Max width for the content on larger screens
const MAX_CONTENT_WIDTH = '480px';

export const guestChargingSx: Record<string, SxProps<Theme>> = {
    root: {
        width: '100%',
        // `dvh`, not `vh`: this page is opened by scanning a code at the charger, so it is almost
        // always a phone browser - and `vh` there counts the address bar, which put the bottom of the
        // page below the bottom of the screen. On a desktop the two units are the same number.
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
    },
    // Wrapper to center content on larger screens
    pageWrapper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        width: '100%',
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        backgroundColor: '#fff',
        boxShadow: 'none',

        '@media (min-width: 600px)': {
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
            minHeight: '100dvh',
        },

        '@media (min-width: 1024px)': {
            marginTop: '24px',
            marginBottom: '24px',
            minHeight: 'calc(100dvh - 48px)',
            borderRadius: '16px',
            overflow: 'hidden',
        },
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',

        '@media (min-width: 600px)': {
            padding: '20px 24px',
        },
    },
    logo: {
        height: '32px',

        '@media (min-width: 600px)': {
            height: '36px',
        },
    },
    content: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        backgroundColor: '#f8f9fa',

        '@media (min-width: 600px)': {
            padding: '24px',
        },
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px 20px',
        marginBottom: '14px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',

        '@media (min-width: 600px)': {
            padding: '24px 24px',
            borderRadius: '16px',
        },
    },
    cardTitle: {
        fontSize: '12px',
        fontWeight: 600,
        color: '#888',
        marginBottom: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',

        '@media (min-width: 600px)': {
            fontSize: '13px',
            marginBottom: '16px',
        },
    },
    locationName: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#333',
        marginBottom: '4px',

        '@media (min-width: 600px)': {
            fontSize: '18px',
        },
    },
    locationAddress: {
        fontSize: '13px',
        color: '#666',

        '@media (min-width: 600px)': {
            fontSize: '14px',
        },
    },
    priceRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '11px 0',
        borderTop: '1px solid #f0f0f0',

        '&:first-of-type': {
            borderTop: 'none',
            paddingTop: 0,
        },

        '&:last-of-type': {
            paddingBottom: 0,
        },
    },
    priceLabel: {
        fontSize: '14px',
        color: '#666',
    },
    priceValue: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#333',

        '@media (min-width: 600px)': {
            fontSize: '17px',
        },
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 500,
    },
    footer: {
        backgroundColor: '#fff',
    },
    primaryButton: {
        width: '100%',
        height: '56px',
        fontSize: '16px',
        fontWeight: 600,
        borderRadius: 0,
        textTransform: 'none',

        '@media (min-width: 600px)': {
            height: '60px',
            fontSize: '17px',
        },
    },
    secondaryButton: {
        width: '100%',
        height: '56px',
        fontSize: '16px',
        fontWeight: 600,
        borderRadius: 0,
        textTransform: 'none',
        backgroundColor: '#f5f5f5',
        color: '#333',

        '&:hover': {
            backgroundColor: '#e0e0e0',
        },

        '@media (min-width: 600px)': {
            height: '60px',
            fontSize: '17px',
        },
    },
    errorButton: {
        width: '100%',
        height: '56px',
        fontSize: '16px',
        fontWeight: 600,
        borderRadius: 0,
        textTransform: 'none',
        backgroundColor: '#d32f2f',
        color: '#fff',

        '&:hover': {
            backgroundColor: '#b71c1c',
        },

        '@media (min-width: 600px)': {
            height: '60px',
            fontSize: '17px',
        },
    },
    successButton: {
        width: '100%',
        height: '56px',
        fontSize: '16px',
        fontWeight: 600,
        borderRadius: 0,
        textTransform: 'none',
        backgroundColor: '#399646',
        color: '#fff',

        '&:hover': {
            backgroundColor: '#2F833C',
        },

        '@media (min-width: 600px)': {
            height: '60px',
            fontSize: '17px',
        },
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '250px',
        gap: '16px',

        '@media (min-width: 600px)': {
            minHeight: '300px',
        },
    },
    loadingText: {
        fontSize: '14px',
        color: '#666',

        '@media (min-width: 600px)': {
            fontSize: '15px',
        },
    },
    progressCard: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '14px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        textAlign: 'center',

        '@media (min-width: 600px)': {
            padding: '38px',
            borderRadius: '16px',
        },
    },
    progressValue: {
        fontSize: '42px',
        fontWeight: 700,
        color: '#1976d2',
        marginBottom: '4px',

        '@media (min-width: 600px)': {
            fontSize: '52px',
        },
    },
    progressUnit: {
        fontSize: '16px',
        color: '#666',
        marginBottom: '16px',

        '@media (min-width: 600px)': {
            fontSize: '18px',
        },
    },
    progressStats: {
        display: 'flex',
        justifyContent: 'space-around',
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: '1px solid #f0f0f0',
    },
    statItem: {
        textAlign: 'center',
    },
    statValue: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#333',

        '@media (min-width: 600px)': {
            fontSize: '20px',
        },
    },
    statLabel: {
        fontSize: '12px',
        color: '#666',
        marginTop: '4px',

        '@media (min-width: 600px)': {
            fontSize: '13px',
        },
    },
    receiptCard: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '22px',
        marginBottom: '14px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',

        '@media (min-width: 600px)': {
            padding: '26px',
            borderRadius: '16px',
        },
    },
    receiptHeader: {
        textAlign: 'center',
        marginBottom: '16px',
    },
    receiptIcon: {
        fontSize: '36px',
        color: '#4caf50',
        marginBottom: '4px',

        '@media (min-width: 600px)': {
            fontSize: '42px',
        },
    },
    receiptTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#333',

        '@media (min-width: 600px)': {
            fontSize: '20px',
        },
    },
    receiptRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '11px 0',
        borderBottom: '1px solid #f0f0f0',

        '&:last-child': {
            borderBottom: 'none',
        },
    },
    receiptLabel: {
        fontSize: '13px',
        color: '#666',
    },
    receiptValue: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#333',
    },
    receiptTotal: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        marginTop: '6px',
        borderTop: '2px solid #333',
    },
    receiptTotalLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#333',

        '@media (min-width: 600px)': {
            fontSize: '16px',
        },
    },
    receiptTotalValue: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#1976d2',

        '@media (min-width: 600px)': {
            fontSize: '22px',
        },
    },
    receiptEmailBlock: {
        marginTop: '18px',
        paddingTop: '18px',
        borderTop: '1px solid #f0f0f0',
    },
    receiptEmailLabel: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#333',
        marginBottom: '2px',
    },
    receiptEmailHint: {
        fontSize: '12px',
        color: '#666',
        marginBottom: '10px',
    },
    receiptEmailInput: {
        marginBottom: '10px',

        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#fafafa',
        },
    },
    receiptEmailButton: {
        textTransform: 'none',
        borderRadius: '8px',
        height: '44px',
        fontSize: '14px',
        fontWeight: 600,
        boxShadow: 'none',
    },
    receiptEmailSent: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: '#2e7d32',
    },
    receiptEmailError: {
        fontSize: '12px',
        color: '#d32f2f',
        marginBottom: '10px',
    },
    errorCard: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '30px',
        marginBottom: '14px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        textAlign: 'center',

        '@media (min-width: 600px)': {
            padding: '38px',
            borderRadius: '16px',
        },
    },
    errorIcon: {
        fontSize: '48px',
        color: '#d32f2f',
        marginBottom: '16px',

        '@media (min-width: 600px)': {
            fontSize: '56px',
        },
    },
    errorTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#333',
        marginBottom: '8px',

        '@media (min-width: 600px)': {
            fontSize: '20px',
        },
    },
    errorMessage: {
        fontSize: '14px',
        color: '#666',

        '@media (min-width: 600px)': {
            fontSize: '15px',
        },
    },
    stripeContainer: {
        marginTop: '16px',
    },
};
